import type { CSSProperties, ReactNode } from 'react';
import { useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';

/**
 * Weidner Via6 AmenitiesGrid section.
 *
 * Mirrors the "Exceptionally crafted comforts for you." block on
 * via6living.com — a heading, a short intro blurb, and an 8-cell icon
 * grid (4 columns on desktop, 2 on tablet, 1 on phones). Each cell
 * pairs one of the via6living SVG glyphs with a short feature label.
 *
 * Each of the eight amenity tiles is composed of two independently-
 * editable pieces:
 *   1. A slot group (`amenitiesgrid.amenity_N`) holding the icon image
 *      and its alt text — clicked / edited as a single unit because
 *      they belong together semantically.
 *   2. A standalone richtext content token (`amenitiesgrid.amenity_N_title`)
 *      for the visible label, so the text has its own edit affordance
 *      and the icon stays a separate click target.
 *
 * All defaults are hard-coded from via6living.com. NO field on this
 * section is mapped to a property's `amenities` collection — the
 * graphics and feature labels render exactly as they appear on
 * via6living and never auto-swap based on inherited property data.
 * Editors can still override any value per-property through the
 * inline editor.
 *
 * Slot groups and per-tile token wrappers are unrolled (not built via
 * .map) because the schema linter requires the contentSchema and each
 * <EditableSlotGroup> / <RichTextContent> path to be statically
 * extractable at preview-render time.
 */

const amenity1Def = {
  label: 'Amenity #1 Icon',
  fields: {
    image: {
      label: 'Icon',
      type: 'image',
      defaultValue: 'https://irp.cdn-website.com/781137b6/dms3rep/multi/3-bd598677.svg',
    },
    alt: {
      label: 'Icon Alt Text',
      type: 'text',
      defaultValue: 'Scenic Swimming Pool with Spa',
    },
  },
};

const amenity2Def = {
  label: 'Amenity #2 Icon',
  fields: {
    image: {
      label: 'Icon',
      type: 'image',
      defaultValue: 'https://irp.cdn-website.com/781137b6/dms3rep/multi/4-fa4590a7.svg',
    },
    alt: {
      label: 'Icon Alt Text',
      type: 'text',
      defaultValue: 'Controlled Access Package Room',
    },
  },
};

const amenity3Def = {
  label: 'Amenity #3 Icon',
  fields: {
    image: {
      label: 'Icon',
      type: 'image',
      defaultValue: 'https://irp.cdn-website.com/781137b6/dms3rep/multi/6-5ca50e5e.svg',
    },
    alt: {
      label: 'Icon Alt Text',
      type: 'text',
      defaultValue: 'Fitness Center',
    },
  },
};

const amenity4Def = {
  label: 'Amenity #4 Icon',
  fields: {
    image: {
      label: 'Icon',
      type: 'image',
      defaultValue: 'https://irp.cdn-website.com/781137b6/dms3rep/multi/8-6796504b.svg',
    },
    alt: {
      label: 'Icon Alt Text',
      type: 'text',
      defaultValue: 'Gourmet Kitchens w/ Energy-Efficient Appliances',
    },
  },
};

const amenity5Def = {
  label: 'Amenity #5 Icon',
  fields: {
    image: {
      label: 'Icon',
      type: 'image',
      defaultValue: 'https://irp.cdn-website.com/781137b6/dms3rep/multi/7-61b13be9.svg',
    },
    alt: {
      label: 'Icon Alt Text',
      type: 'text',
      defaultValue: 'Outside Deck with Grills',
    },
  },
};

const amenity6Def = {
  label: 'Amenity #6 Icon',
  fields: {
    image: {
      label: 'Icon',
      type: 'image',
      defaultValue: 'https://irp.cdn-website.com/781137b6/dms3rep/multi/5-64e5c9c3.svg',
    },
    alt: {
      label: 'Icon Alt Text',
      type: 'text',
      defaultValue: 'Pet Spa and Relief Area',
    },
  },
};

const amenity7Def = {
  label: 'Amenity #7 Icon',
  fields: {
    image: {
      label: 'Icon',
      type: 'image',
      defaultValue: 'https://irp.cdn-website.com/781137b6/dms3rep/multi/2-777bf817.svg',
    },
    alt: {
      label: 'Icon Alt Text',
      type: 'text',
      defaultValue: 'Walk to Work! In the Heart of Downtown and South Lake Union',
    },
  },
};

const amenity8Def = {
  label: 'Amenity #8 Icon',
  fields: {
    image: {
      label: 'Icon',
      type: 'image',
      defaultValue: 'https://irp.cdn-website.com/781137b6/dms3rep/multi/1-182134b8.svg',
    },
    alt: {
      label: 'Icon Alt Text',
      type: 'text',
      defaultValue: 'Walk-In Closets*',
    },
  },
};

/** Shared className for every tile's title — kept in one place so the
 *  eight unrolled <RichTextContent> wrappers below stay in sync. */
const TILE_TITLE_CLASS =
  'mt-3 font-body text-xs sm:text-sm font-medium tracking-wide ' +
  'text-[var(--vibe-color-text-on-primary)] leading-snug ' +
  '[&_p]:m-0 [&_strong]:font-semibold [&_b]:font-semibold [&_a]:underline';

const viewAmenitiesCtaDef = {
  label: 'View All Amenities CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/amenities' },
    label: { label: 'Label', type: 'text', defaultValue: 'View All Amenities' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const contactCtaDef = {
  label: 'Contact Us CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/contact-us' },
    label: { label: 'Label', type: 'text', defaultValue: 'Contact Us' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

interface AmenityIconProps {
  data: { image?: string; alt?: string };
}

/**
 * Renders just the icon image inside a tile. Used as the child of an
 * <EditableSlotGroup> so the slot group's clickable region is limited
 * to the icon — not the title, which gets its own RichTextContent
 * wrapper sibling to the icon.
 *
 * The SVG is rendered as a CSS mask (instead of as a bitmap-style
 * image element) so the glyph is tinted by
 * `background-color: var(--vibe-color-secondary)` and tracks the
 * theme's secondary color rather than the hard-coded fill that ships
 * inside the source SVG.
 */
function AmenityIcon({ data }: AmenityIconProps) {
  const src = data.image || '';
  const alt = data.alt || 'Amenity icon';

  if (!src) {
    return <div className="amenities-grid-icon-wrap" />;
  }

  // Inline style is required so the mask URL tracks the per-tile
  // editable `image` slot field instead of being baked into static CSS.
  const maskStyle: CSSProperties = {
    WebkitMaskImage: `url(${src})`,
    maskImage: `url(${src})`,
  };

  return (
    <div className="amenities-grid-icon-wrap">
      <div
        role="img"
        aria-label={alt}
        className="amenities-grid-icon"
        style={maskStyle}
      />
    </div>
  );
}

/** Visual tile container — no editor wrapping; just layout + background. */
function AmenityTile({ children }: { children: ReactNode }) {
  return (
    <div className="w-full aspect-square flex flex-col items-center justify-center text-center px-3 py-5 sm:px-5 sm:py-7 bg-[var(--vibe-color-primary)]">
      {children}
    </div>
  );
}

export function AmenitiesGrid() {
  // Lazy-load the "Satisfy" cursive Google Font used by the
  // .vibe-cursive-accent span in the heading. De-duped against
  // PropertyOverview / FloorplansList / Header by the data-vibe-font
  // marker so we never inject the stylesheet link more than once.
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="satisfy"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Satisfy&display=swap';
    link.setAttribute('data-vibe-font', 'satisfy');
    document.head.appendChild(link);
  }, []);

  const a1 = useSlotGroup('amenitiesgrid.amenity_1', amenity1Def);
  const a2 = useSlotGroup('amenitiesgrid.amenity_2', amenity2Def);
  const a3 = useSlotGroup('amenitiesgrid.amenity_3', amenity3Def);
  const a4 = useSlotGroup('amenitiesgrid.amenity_4', amenity4Def);
  const a5 = useSlotGroup('amenitiesgrid.amenity_5', amenity5Def);
  const a6 = useSlotGroup('amenitiesgrid.amenity_6', amenity6Def);
  const a7 = useSlotGroup('amenitiesgrid.amenity_7', amenity7Def);
  const a8 = useSlotGroup('amenitiesgrid.amenity_8', amenity8Def);
  const viewAmenitiesCta = useSlotGroup(
    'amenitiesgrid.view_amenities_cta',
    viewAmenitiesCtaDef,
  );
  const contactCta = useSlotGroup('amenitiesgrid.contact_cta', contactCtaDef);

  return (
    <section className="py-20 md:py-28 bg-[var(--vibe-color-primary)]">
      <div className="max-w-7xl xl:max-w-[88rem] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading + description + CTAs — mirrors the PropertyOverview
            asymmetric grid (2/5 heading on the left, 3/5 blurb + CTAs
            on the right at >=lg; stacked vertically below that). */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-2">
            <RichTextContent
              path="amenitiesgrid.heading"
              as="h2"
              className="font-heading text-4xl sm:text-5xl md:text-6xl font-normal text-[var(--vibe-color-text-on-primary)] leading-tight uppercase [&_strong]:font-normal [&_b]:font-normal [&_br]:hidden lg:[&_br]:inline"
            />
          </div>

          <div className="lg:col-span-3">
            <RichTextContent
              path="amenitiesgrid.description"
              as="div"
              className="font-body text-sm sm:text-base font-normal text-[var(--vibe-color-text-on-primary)] leading-relaxed [&_a]:text-[var(--vibe-color-text-on-primary)] [&_a]:underline [&_strong]:font-normal [&_b]:font-normal"
            />

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <EditableSlotGroup
                groupPath="amenitiesgrid.view_amenities_cta"
                definition={viewAmenitiesCtaDef}
              >
                <a
                  href={viewAmenitiesCta.url || '/amenities'}
                  target={viewAmenitiesCta._target || '_self'}
                  className="inline-block text-center w-[18rem] px-6 py-2.5 text-sm font-medium tracking-[0.2em] uppercase rounded-full bg-[var(--vibe-color-secondary)] text-[var(--vibe-color-text-on-primary)] border border-[var(--vibe-color-secondary)] hover:bg-white hover:text-[var(--vibe-color-secondary)] hover:border-white transition-all"
                >
                  {viewAmenitiesCta.label || 'View All Amenities'}
                </a>
              </EditableSlotGroup>

              <EditableSlotGroup
                groupPath="amenitiesgrid.contact_cta"
                definition={contactCtaDef}
              >
                <a
                  href={contactCta.url || '/contact-us'}
                  target={contactCta._target || '_self'}
                  className="inline-block text-center w-[18rem] px-6 py-2.5 text-sm font-medium tracking-[0.2em] uppercase rounded-full border border-[var(--vibe-color-text-on-primary)] text-[var(--vibe-color-text-on-primary)] hover:bg-[var(--vibe-color-secondary)] hover:text-[var(--vibe-color-text-on-primary)] hover:border-white transition-all"
                >
                  {contactCta.label || 'Contact Us'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--vibe-color-text-on-primary)] border border-[var(--vibe-color-text-on-primary)]">
          {/* Each tile has two independent editable regions: the icon
              (slot group) and the title (richtext token). Both paths
              are written as string literals so the schema linter can
              statically extract them. */}
          <AmenityTile>
            <EditableSlotGroup groupPath="amenitiesgrid.amenity_1" definition={amenity1Def}>
              <AmenityIcon data={a1} />
            </EditableSlotGroup>
            <RichTextContent path="amenitiesgrid.amenity_1_title" as="div" className={TILE_TITLE_CLASS} />
          </AmenityTile>
          <AmenityTile>
            <EditableSlotGroup groupPath="amenitiesgrid.amenity_2" definition={amenity2Def}>
              <AmenityIcon data={a2} />
            </EditableSlotGroup>
            <RichTextContent path="amenitiesgrid.amenity_2_title" as="div" className={TILE_TITLE_CLASS} />
          </AmenityTile>
          <AmenityTile>
            <EditableSlotGroup groupPath="amenitiesgrid.amenity_3" definition={amenity3Def}>
              <AmenityIcon data={a3} />
            </EditableSlotGroup>
            <RichTextContent path="amenitiesgrid.amenity_3_title" as="div" className={TILE_TITLE_CLASS} />
          </AmenityTile>
          <AmenityTile>
            <EditableSlotGroup groupPath="amenitiesgrid.amenity_4" definition={amenity4Def}>
              <AmenityIcon data={a4} />
            </EditableSlotGroup>
            <RichTextContent path="amenitiesgrid.amenity_4_title" as="div" className={TILE_TITLE_CLASS} />
          </AmenityTile>
          <AmenityTile>
            <EditableSlotGroup groupPath="amenitiesgrid.amenity_5" definition={amenity5Def}>
              <AmenityIcon data={a5} />
            </EditableSlotGroup>
            <RichTextContent path="amenitiesgrid.amenity_5_title" as="div" className={TILE_TITLE_CLASS} />
          </AmenityTile>
          <AmenityTile>
            <EditableSlotGroup groupPath="amenitiesgrid.amenity_6" definition={amenity6Def}>
              <AmenityIcon data={a6} />
            </EditableSlotGroup>
            <RichTextContent path="amenitiesgrid.amenity_6_title" as="div" className={TILE_TITLE_CLASS} />
          </AmenityTile>
          <AmenityTile>
            <EditableSlotGroup groupPath="amenitiesgrid.amenity_7" definition={amenity7Def}>
              <AmenityIcon data={a7} />
            </EditableSlotGroup>
            <RichTextContent path="amenitiesgrid.amenity_7_title" as="div" className={TILE_TITLE_CLASS} />
          </AmenityTile>
          <AmenityTile>
            <EditableSlotGroup groupPath="amenitiesgrid.amenity_8" definition={amenity8Def}>
              <AmenityIcon data={a8} />
            </EditableSlotGroup>
            <RichTextContent path="amenitiesgrid.amenity_8_title" as="div" className={TILE_TITLE_CLASS} />
          </AmenityTile>
        </div>
      </div>

      <style>{`
        /* Cursive accent for the heading's <span class="vibe-cursive-accent">
           word — Satisfy is lazy-loaded in the useEffect above. The
           text-transform:none escape lets the script glyphs render in
           their natural mixed case even though the h2 is uppercase. */
        .vibe-cursive-accent {
          font-family: 'Satisfy', cursive;
          color: var(--vibe-color-secondary);
          font-weight: 400;
          text-transform: none;
          letter-spacing: 0;
        }
        .amenities-grid-icon-wrap {
          width: 72px;
          height: 72px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        /* SVG glyph is applied as a CSS mask so the visible color comes
           from background-color (tied to the theme secondary token)
           instead of the source SVG's baked-in fill. */
        .amenities-grid-icon {
          width: 100%;
          height: 100%;
          display: block;
          background-color: var(--vibe-color-secondary);
          -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
          -webkit-mask-position: center;
          mask-position: center;
          -webkit-mask-size: contain;
          mask-size: contain;
        }
        @media (max-width: 640px) {
          .amenities-grid-icon-wrap {
            width: 56px;
            height: 56px;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesGrid',
  displayName: 'Amenities Grid',
  tokens: {
    'amenitiesgrid.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue:
        'Exceptionally <br />crafted <span class="vibe-cursive-accent">comforts</span> <br />for you.',
    },
    'amenitiesgrid.description': {
      label: 'Description',
      type: 'richtext',
      defaultValue:
        "<p>Via6 is not just a home – it's a neighborhood. Living here gives you access to exclusive community perks, including 24-hour concierge services, a cutting-edge fitness center, a pet spa and relief area, an outside deck with grills, and over 12,000 square feet of premium retail just outside your door.</p>",
    },
    'amenitiesgrid.amenity_1_title': {
      label: 'Amenity #1 Title',
      type: 'richtext',
      defaultValue: '<p>Scenic Swimming Pool with Spa</p>',
    },
    'amenitiesgrid.amenity_2_title': {
      label: 'Amenity #2 Title',
      type: 'richtext',
      defaultValue: '<p>Controlled Access Package Room</p>',
    },
    'amenitiesgrid.amenity_3_title': {
      label: 'Amenity #3 Title',
      type: 'richtext',
      defaultValue: '<p>Fitness Center</p>',
    },
    'amenitiesgrid.amenity_4_title': {
      label: 'Amenity #4 Title',
      type: 'richtext',
      defaultValue: '<p>Gourmet Kitchens w/ Energy-Efficient Appliances</p>',
    },
    'amenitiesgrid.amenity_5_title': {
      label: 'Amenity #5 Title',
      type: 'richtext',
      defaultValue: '<p>Outside Deck with Grills</p>',
    },
    'amenitiesgrid.amenity_6_title': {
      label: 'Amenity #6 Title',
      type: 'richtext',
      defaultValue: '<p>Pet Spa and Relief Area</p>',
    },
    'amenitiesgrid.amenity_7_title': {
      label: 'Amenity #7 Title',
      type: 'richtext',
      defaultValue: '<p>Walk to Work! In the Heart of Downtown and South Lake Union</p>',
    },
    'amenitiesgrid.amenity_8_title': {
      label: 'Amenity #8 Title',
      type: 'richtext',
      defaultValue: '<p>Walk-In Closets*</p>',
    },
  },
  slotGroups: {
    'amenitiesgrid.amenity_1': amenity1Def,
    'amenitiesgrid.amenity_2': amenity2Def,
    'amenitiesgrid.amenity_3': amenity3Def,
    'amenitiesgrid.amenity_4': amenity4Def,
    'amenitiesgrid.amenity_5': amenity5Def,
    'amenitiesgrid.amenity_6': amenity6Def,
    'amenitiesgrid.amenity_7': amenity7Def,
    'amenitiesgrid.amenity_8': amenity8Def,
    'amenitiesgrid.view_amenities_cta': viewAmenitiesCtaDef,
    'amenitiesgrid.contact_cta': contactCtaDef,
  },
};

(AmenitiesGrid as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default AmenitiesGrid;
